import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import { BackIcon } from '../components/AppHeader';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import LanguageIcon from '@mui/icons-material/Language';
import { green, red } from '@mui/material/colors';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { kebabCase } from 'lodash-es';
import * as api from '../api';
import { useAuth } from '../auth/AuthContext';
import LANGUAGES from '../i18n/languages';

const TOTAL_STEPS = 9;
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

export default function NewMatch() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, signIn, signUp, signInWithGoogle, forceChangePassword, forgotPassword, confirmForgotPassword } = useAuth();

  const [step, setStep] = useState(0);
  const [matchLocale, setMatchLocale] = useState(null); // 'en-US' | 'he-IL'
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(moment());
  const [description, setDescription] = useState('');
  const [registration, setRegistration] = useState(null);
  const [squadSelection, setSquadSelection] = useState(null);
  const [ipscLevel, setIpscLevel] = useState(2);
  const [alias, setAlias] = useState('');
  const [aliasStatus, setAliasStatus] = useState(null); // null | 'checking' | 'available' | 'taken' | 'invalid'

  // Auth form (for non-authenticated users)
  const [authMode, setAuthMode] = useState('signin'); // 'signin' | 'signup'
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authName, setAuthName] = useState('');
  const [acceptTos, setAcceptTos] = useState(false);

  // Force change password / forgot password state
  const [forceChangeSession, setForceChangeSession] = useState(null);
  const [forceChangeEmail, setForceChangeEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [authSubMode, setAuthSubMode] = useState(null); // null | 'force-change' | 'forgot' | 'reset'
  const [resetCode, setResetCode] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [createdAlias, setCreatedAlias] = useState('');

  const handlePickLanguage = (lang) => {
    setMatchLocale(lang.locale);
    i18n.changeLanguage(lang.code);
    localStorage.setItem('userLanguage', lang.code);
    moment.locale(lang.code);
    setStep(1);
  };

  // Auto-generate alias from title
  useEffect(() => {
    if (step <= 6 && title) {
      setAlias(kebabCase(title));
    }
  }, [title, step]);

  // Check alias availability with debounce
  const checkAlias = useCallback(async (value) => {
    if (!value) {
      setAliasStatus(null);
      return;
    }
    if (encodeURIComponent(value) !== value || value.startsWith('demo') || value === 'new' || value === 'signup' || value === 'signin') {
      setAliasStatus('invalid');
      return;
    }
    setAliasStatus('checking');
    try {
      await api.getMatch(value);
      setAliasStatus('taken');
    } catch {
      setAliasStatus('available');
    }
  }, []);

  useEffect(() => {
    if (step === 7) {
      const timer = setTimeout(() => checkAlias(alias), 500);
      return () => clearTimeout(timer);
    }
  }, [alias, step, checkAlias]);

  const handleForceChange = async () => {
    if (!newPassword) { setError(t('auth.setNewPassword')); return; }
    if (newPassword !== newPasswordConfirm) { setError(t('auth.passwordsDoNotMatch')); return; }
    setCreating(true);
    setError('');
    try {
      await forceChangePassword({ email: forceChangeEmail, newPassword, session: forceChangeSession });
      setAuthSubMode(null);
      setForceChangeSession(null);
      // Now create the match since auth succeeded
      const matchData = {
        title,
        description: description || title,
        locale: matchLocale,
        ipscLevel,
        alias,
        startDate: { day: date.date(), month: date.month() + 1, year: date.year() },
        endDate: { day: date.date(), month: date.month() + 1, year: date.year() },
        registration: registration || 'closed',
        squadSelection: squadSelection || 'closed',
        squadsPublished: false,
        scoresPublished: null,
        stagesPublished: false,
      };
      await api.createMatch(matchData);
      setCreatedAlias(alias);
      setStep(TOTAL_STEPS);
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.error || err.message || t('common.saveError');
      setError(message);
    } finally {
      setCreating(false);
    }
  };

  const handleForgotRequest = async () => {
    if (!authEmail) { setError(t('auth.emailError')); return; }
    setCreating(true);
    setError('');
    try {
      await forgotPassword({ email: authEmail });
      setAuthSubMode('reset');
      setAuthSuccess(t('auth.resetCodeSent'));
    } catch (err) {
      setError(err.response?.data?.detail || t('common.saveError'));
    } finally {
      setCreating(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetCode || !newPassword) { setError(t('auth.verificationCode')); return; }
    if (newPassword !== newPasswordConfirm) { setError(t('auth.passwordsDoNotMatch')); return; }
    setCreating(true);
    setError('');
    try {
      await confirmForgotPassword({ email: authEmail, code: resetCode, newPassword });
      setAuthSubMode(null);
      // Now create the match since auth succeeded
      const matchData = {
        title,
        description: description || title,
        locale: matchLocale,
        ipscLevel,
        alias,
        startDate: { day: date.date(), month: date.month() + 1, year: date.year() },
        endDate: { day: date.date(), month: date.month() + 1, year: date.year() },
        registration: registration || 'closed',
        squadSelection: squadSelection || 'closed',
        squadsPublished: false,
        scoresPublished: null,
        stagesPublished: false,
      };
      await api.createMatch(matchData);
      setCreatedAlias(alias);
      setStep(TOTAL_STEPS);
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.error || err.message || t('common.saveError');
      setError(message);
    } finally {
      setCreating(false);
    }
  };

  // Latest form values for the Google callback (which is registered once with GIS)
  const googleCtxRef = useRef(null);
  googleCtxRef.current = {
    title, description, matchLocale, ipscLevel, alias, date, registration, squadSelection, acceptTos,
  };

  const handleGoogleCreate = useCallback(async (response) => {
    const ctx = googleCtxRef.current;
    if (!ctx.acceptTos) {
      setError(t('newMatch.tosRequired', 'Please accept the terms to continue'));
      return;
    }
    setCreating(true);
    setError('');
    try {
      await signInWithGoogle({ idToken: response.credential });
      const matchData = {
        title: ctx.title,
        description: ctx.description || ctx.title,
        locale: ctx.matchLocale,
        ipscLevel: ctx.ipscLevel,
        alias: ctx.alias,
        startDate: { day: ctx.date.date(), month: ctx.date.month() + 1, year: ctx.date.year() },
        endDate: { day: ctx.date.date(), month: ctx.date.month() + 1, year: ctx.date.year() },
        registration: ctx.registration || 'closed',
        squadSelection: ctx.squadSelection || 'closed',
        squadsPublished: false,
        scoresPublished: null,
        stagesPublished: false,
      };
      await api.createMatch(matchData);
      setCreatedAlias(ctx.alias);
      setStep(TOTAL_STEPS);
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.error || err.message || t('common.saveError');
      setError(message);
    } finally {
      setCreating(false);
    }
  }, [signInWithGoogle, t]);

  // Initialise the GIS button on step 8
  useEffect(() => {
    if (step !== 8 || authSubMode) return undefined;
    if (!GOOGLE_CLIENT_ID) return undefined;
    let cancelled = false;
    const init = () => {
      if (cancelled) return true;
      if (!window.google?.accounts?.id) return false;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCreate,
      });
      const el = document.getElementById('new-match-google-btn');
      if (el) {
        el.innerHTML = '';
        window.google.accounts.id.renderButton(el, {
          theme: 'outline', size: 'large', width: 280, text: 'continue_with',
        });
      }
      return true;
    };
    if (!init()) {
      const interval = setInterval(() => { if (init()) clearInterval(interval); }, 200);
      return () => { cancelled = true; clearInterval(interval); };
    }
    return () => { cancelled = true; };
  }, [step, authSubMode, handleGoogleCreate]);

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      if (!isAuthenticated) {
        if (authMode === 'signup') {
          await signUp({ email: authEmail, password: authPassword, name: authName || authEmail });
        } else {
          try {
            await signIn({ email: authEmail, password: authPassword });
          } catch (err) {
            if (err.challenge === 'NEW_PASSWORD_REQUIRED') {
              setForceChangeSession(err.session);
              setForceChangeEmail(authEmail);
              setAuthSubMode('force-change');
              setCreating(false);
              return;
            }
            throw err;
          }
        }
      }

      const matchData = {
        title,
        description: description || title,
        locale: matchLocale,
        ipscLevel,
        alias,
        startDate: { day: date.date(), month: date.month() + 1, year: date.year() },
        endDate: { day: date.date(), month: date.month() + 1, year: date.year() },
        registration: registration || 'closed',
        squadSelection: squadSelection || 'closed',
        squadsPublished: false,
        scoresPublished: null,
        stagesPublished: false,
      };

      await api.createMatch(matchData);
      setCreatedAlias(alias);
      setStep(TOTAL_STEPS);
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.error || err.message || t('common.saveError');
      setError(message);
    } finally {
      setCreating(false);
    }
  };

  const progress = step === 0 ? 0 : Math.round((step / TOTAL_STEPS) * 100);
  const canGoBack = step > 0 && step < TOTAL_STEPS;

  const goBack = () => {
    if (step === 8 && isAuthenticated) {
      setStep(7);
    } else {
      setStep((s) => s - 1);
    }
  };

  const goNext = (nextStep) => {
    if (nextStep !== undefined) {
      setStep(nextStep);
    } else if (step === 7 && isAuthenticated) {
      handleCreate();
    } else {
      setStep((s) => s + 1);
    }
  };

  // Step 0 (language) has a special header - no progress bar, bilingual title
  const isLangStep = step === 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
        color: 'white',
        px: 2, pt: 2, pb: 3,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {canGoBack ? (
            <IconButton onClick={goBack} sx={{ color: 'white', ml: -1 }}>
              <BackIcon />
            </IconButton>
          ) : (
            <IconButton onClick={() => navigate('/')} sx={{ color: 'rgba(255,255,255,0.7)', ml: -1 }}>
              <CloseIcon />
            </IconButton>
          )}
          <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
            {isLangStep ? 'Create a Match' : step < TOTAL_STEPS ? t('newMatch.title') : t('newMatch.doneTitle')}
          </Typography>
        </Box>
        {!isLangStep && step < TOTAL_STEPS && (
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {t('newMatch.step', { current: step, total: TOTAL_STEPS - 1 })}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {progress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 4, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.15)',
                '& .MuiLinearProgress-bar': { bgcolor: 'white', borderRadius: 2 },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Step content */}
      <Box sx={{ px: 2, mt: -1.5 }}>
        {/* Step 0: Language */}
        {step === 0 && (
          <Paper sx={{ p: 3, borderRadius: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <LanguageIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Choose your language
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
              This will be the language of your match website
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {LANGUAGES.map((lang) => (
                <ButtonBase
                  key={lang.code}
                  onClick={() => handlePickLanguage(lang)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 2,
                    p: 2, borderRadius: 2.5,
                    border: '2px solid', borderColor: 'divider',
                    textAlign: 'start', width: '100%',
                    transition: 'border-color 0.15s',
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                >
                  <Typography sx={{ fontSize: 32 }}>{lang.flag}</Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
                    {lang.label}
                  </Typography>
                </ButtonBase>
              ))}
            </Box>
          </Paper>
        )}

        {/* Step 1: Name */}
        {step === 1 && (
          <StepCard
            title={t('newMatch.nameTitle')}
            subtitle={t('newMatch.nameSubtitle')}
          >
            <TextField
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('newMatch.namePlaceholder')}
              fullWidth
              autoFocus
              size="small"
              sx={{ mb: 2.5 }}
            />
            <Button
              variant="contained"
              fullWidth
              disabled={!title.trim()}
              onClick={() => goNext()}
              sx={btnStyle}
            >
              {t('common.next')}
            </Button>
          </StepCard>
        )}

        {/* Step 2: Date */}
        {step === 2 && (
          <StepCard
            title={t('newMatch.dateTitle')}
            subtitle={t('newMatch.dateSubtitle')}
          >
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                value={date}
                onChange={(v) => v && setDate(v)}
                minDate={moment()}
                format="LL"
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                sx={{ mb: 2.5 }}
              />
            </LocalizationProvider>
            <Button
              variant="contained"
              fullWidth
              onClick={() => goNext()}
              sx={btnStyle}
            >
              {t('common.next')}
            </Button>
          </StepCard>
        )}

        {/* Step 3: Description */}
        {step === 3 && (
          <StepCard
            title={t('newMatch.descTitle')}
            subtitle={t('newMatch.descSubtitle')}
          >
            <TextField
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('newMatch.descPlaceholder')}
              fullWidth
              multiline
              minRows={2}
              size="small"
              sx={{ mb: 2.5 }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
              {t('newMatch.descTip')}
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={() => goNext()}
              sx={btnStyle}
            >
              {description.trim() ? t('common.next') : t('newMatch.skip')}
            </Button>
          </StepCard>
        )}

        {/* Step 4: Registration */}
        {step === 4 && (
          <StepCard
            title={t('newMatch.regTitle')}
            subtitle={t('newMatch.regSubtitle')}
          >
            <Button
              variant="contained"
              fullWidth
              onClick={() => { setRegistration('open'); goNext(); }}
              sx={{ ...btnStyle, mb: 1.5 }}
            >
              {t('newMatch.regOpen')}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => { setRegistration('closed'); goNext(); }}
              sx={{ borderRadius: 2.5, py: 1.5, textTransform: 'none' }}
            >
              {t('newMatch.regClosed')}
            </Button>
          </StepCard>
        )}

        {/* Step 5: Squad Selection */}
        {step === 5 && (
          <StepCard
            title={t('newMatch.squadTitle')}
            subtitle={t('newMatch.squadSubtitle')}
          >
            <Button
              variant="contained"
              fullWidth
              onClick={() => { setSquadSelection('during-registration'); goNext(); }}
              sx={{ ...btnStyle, mb: 1.5 }}
            >
              {t('newMatch.squadDuring')}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => { setSquadSelection('after-payment'); goNext(); }}
              sx={{ borderRadius: 2.5, py: 1.5, textTransform: 'none', mb: 1.5 }}
            >
              {t('newMatch.squadAfter')}
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={() => { setSquadSelection('closed'); goNext(); }}
              sx={{ textTransform: 'none' }}
            >
              {t('newMatch.squadClosed')}
            </Button>
          </StepCard>
        )}

        {/* Step 6: IPSC Level */}
        {step === 6 && (
          <StepCard
            title={t('newMatch.levelTitle')}
            subtitle={t('newMatch.levelSubtitle')}
          >
            <Select
              value={ipscLevel}
              onChange={(e) => setIpscLevel(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 2.5 }}
            >
              {[0, 1, 2, 3, 4, 5].map((lv) => (
                <MenuItem key={lv} value={lv}>{t(`app.ipsclevel${lv}`)}</MenuItem>
              ))}
            </Select>
            <Button
              variant="contained"
              fullWidth
              onClick={() => goNext()}
              sx={btnStyle}
            >
              {t('common.next')}
            </Button>
          </StepCard>
        )}

        {/* Step 7: Alias */}
        {step === 7 && (
          <StepCard
            title={t('newMatch.aliasTitle')}
            subtitle={t('newMatch.aliasSubtitle')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, direction: 'ltr' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                endofscoring.com/
              </Typography>
              <TextField
                value={alias}
                onChange={(e) => setAlias(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                size="small"
                fullWidth
                placeholder="my-match"
                inputProps={{ style: { direction: 'ltr' } }}
              />
            </Box>
            {aliasStatus === 'checking' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="caption">{t('newMatch.aliasChecking')}</Typography>
              </Box>
            )}
            {aliasStatus === 'available' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2, color: green[600] }}>
                <CheckCircleIcon sx={{ fontSize: 18 }} />
                <Typography variant="caption" sx={{ color: 'inherit' }}>{t('newMatch.aliasAvailable')}</Typography>
              </Box>
            )}
            {aliasStatus === 'taken' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2, color: red[600] }}>
                <CloseIcon sx={{ fontSize: 18 }} />
                <Typography variant="caption" sx={{ color: 'inherit' }}>{t('newMatch.aliasTaken')}</Typography>
              </Box>
            )}
            {aliasStatus === 'invalid' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2, color: red[600] }}>
                <CloseIcon sx={{ fontSize: 18 }} />
                <Typography variant="caption" sx={{ color: 'inherit' }}>{t('newMatch.aliasInvalid')}</Typography>
              </Box>
            )}
            <Button
              variant="contained"
              fullWidth
              disabled={!alias || aliasStatus !== 'available'}
              onClick={() => goNext()}
              sx={btnStyle}
            >
              {isAuthenticated ? t('newMatch.create') : t('common.next')}
            </Button>
            {creating && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={28} />
              </Box>
            )}
          </StepCard>
        )}

        {/* Step 8: Auth (only shown if not authenticated) */}
        {step === 8 && authSubMode === 'force-change' && (
          <StepCard
            title={t('auth.setNewPassword')}
            subtitle={t('auth.newPasswordRequired')}
          >
            <TextField
              label={t('auth.setNewPassword')}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              size="small"
              type="password"
              sx={{ mb: 1.5 }}
            />
            <TextField
              label={t('auth.confirmNewPassword')}
              value={newPasswordConfirm}
              onChange={(e) => setNewPasswordConfirm(e.target.value)}
              fullWidth
              size="small"
              type="password"
              sx={{ mb: 1.5 }}
            />
            <Button
              variant="contained"
              fullWidth
              disabled={!newPassword || creating}
              onClick={handleForceChange}
              sx={btnStyle}
            >
              {creating ? <CircularProgress size={24} color="inherit" /> : t('auth.setNewPassword')}
            </Button>
          </StepCard>
        )}
        {step === 8 && authSubMode === 'forgot' && (
          <StepCard
            title={t('auth.forgotPasswordTitle')}
            subtitle={t('auth.forgotPasswordDescription')}
          >
            <TextField
              label={t('auth.email')}
              value={authEmail}
              onChange={(e) => setAuthEmail(e.target.value)}
              fullWidth
              size="small"
              type="email"
              sx={{ mb: 1.5 }}
            />
            <Button
              variant="contained"
              fullWidth
              disabled={!authEmail || creating}
              onClick={handleForgotRequest}
              sx={{ ...btnStyle, mb: 1.5 }}
            >
              {creating ? <CircularProgress size={24} color="inherit" /> : t('auth.sendResetCode')}
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={() => { setAuthSubMode(null); setError(''); }}
              sx={{ textTransform: 'none' }}
            >
              {t('auth.backToSignIn')}
            </Button>
          </StepCard>
        )}
        {step === 8 && authSubMode === 'reset' && (
          <StepCard
            title={t('auth.resetPasswordTitle')}
            subtitle={authSuccess || t('auth.resetCodeSent')}
          >
            <TextField
              label={t('auth.verificationCode')}
              value={resetCode}
              onChange={(e) => setResetCode(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 1.5 }}
            />
            <TextField
              label={t('auth.setNewPassword')}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              size="small"
              type="password"
              sx={{ mb: 1.5 }}
            />
            <TextField
              label={t('auth.confirmNewPassword')}
              value={newPasswordConfirm}
              onChange={(e) => setNewPasswordConfirm(e.target.value)}
              fullWidth
              size="small"
              type="password"
              sx={{ mb: 1.5 }}
            />
            <Button
              variant="contained"
              fullWidth
              disabled={!resetCode || !newPassword || creating}
              onClick={handleResetPassword}
              sx={{ ...btnStyle, mb: 1.5 }}
            >
              {creating ? <CircularProgress size={24} color="inherit" /> : t('auth.resetPassword')}
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={() => { setAuthSubMode(null); setError(''); setAuthSuccess(''); }}
              sx={{ textTransform: 'none' }}
            >
              {t('auth.backToSignIn')}
            </Button>
          </StepCard>
        )}
        {step === 8 && !authSubMode && (
          <StepCard
            title={t('newMatch.authTitle')}
            subtitle={t('newMatch.authSubtitle')}
          >
            <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
              <Button
                variant={authMode === 'signin' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setAuthMode('signin')}
                sx={{ flex: 1, borderRadius: 2, textTransform: 'none' }}
              >
                {t('auth.signIn')}
              </Button>
              <Button
                variant={authMode === 'signup' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setAuthMode('signup')}
                sx={{ flex: 1, borderRadius: 2, textTransform: 'none' }}
              >
                {t('auth.signUp')}
              </Button>
            </Box>
            {authMode === 'signup' && (
              <TextField
                label={t('newMatch.authName')}
                value={authName}
                onChange={(e) => setAuthName(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 1.5 }}
              />
            )}
            <TextField
              label={t('auth.email')}
              value={authEmail}
              onChange={(e) => setAuthEmail(e.target.value)}
              fullWidth
              size="small"
              type="email"
              sx={{ mb: 1.5 }}
            />
            <TextField
              label={t('auth.password')}
              value={authPassword}
              onChange={(e) => setAuthPassword(e.target.value)}
              fullWidth
              size="small"
              type="password"
              sx={{ mb: 1.5 }}
            />
            <FormControlLabel
              control={<Checkbox checked={acceptTos} onChange={() => setAcceptTos(!acceptTos)} />}
              label={
                <Typography variant="caption">
                  {t('newMatch.tosAgree')}{' '}
                  <a href="https://docs.google.com/document/d/1Saeek_HSNjO4_yW6FvtkQHOAFplhmxEs1Ctd-UMYOto/edit?usp=sharing" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                    {t('newMatch.tosTerms')}
                  </a>
                  {' '}{t('newMatch.tosAnd')}{' '}
                  <a href="https://docs.google.com/document/d/1l7tK7PK0I3BYNuFC104ipvMhWIMETxahUi690oL_Wr0/edit?usp=sharing" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                    {t('newMatch.tosPrivacy')}
                  </a>
                </Typography>
              }
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              fullWidth
              disabled={!authEmail || !authPassword || !acceptTos || creating}
              onClick={handleCreate}
              sx={btnStyle}
            >
              {creating ? <CircularProgress size={24} color="inherit" /> : t('newMatch.create')}
            </Button>
            {authMode === 'signin' && (
              <Button
                variant="text"
                fullWidth
                onClick={() => { setAuthSubMode('forgot'); setError(''); }}
                sx={{ textTransform: 'none', mt: 1 }}
              >
                {t('auth.forgotPassword')}
              </Button>
            )}
            <Divider sx={{ my: 2 }}>{t('manage.or', 'or')}</Divider>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box id="new-match-google-btn" />
            </Box>
          </StepCard>
        )}

        {/* Step 9: Done */}
        {step === TOTAL_STEPS && (
          <StepCard
            title={t('newMatch.doneHeading')}
            subtitle={t('newMatch.doneSubtitle')}
          >
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mb: 3,
            }}>
              <Box sx={{
                width: 80, height: 80, borderRadius: '50%',
                bgcolor: green[50],
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CheckCircleIcon sx={{ fontSize: 48, color: green[500] }} />
              </Box>
            </Box>
            <Paper sx={{
              bgcolor: '#f5f5f5', borderRadius: 2, p: 2, mb: 2.5, textAlign: 'center',
            }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('newMatch.doneUrl')}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 0.5, direction: 'ltr' }}>
                endofscoring.com/{createdAlias}
              </Typography>
            </Paper>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate(`/${createdAlias}/manage`)}
              sx={{ ...btnStyle, mb: 1.5 }}
            >
              {t('newMatch.doneDashboard')}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate(`/${createdAlias}`)}
              sx={{ borderRadius: 2.5, py: 1.5, textTransform: 'none' }}
            >
              {t('newMatch.doneViewSite')}
            </Button>
          </StepCard>
        )}
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError('')} severity="error" variant="filled" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const btnStyle = {
  borderRadius: 2.5, py: 1.5, textTransform: 'none', fontWeight: 600,
};

function StepCard({ title, subtitle, children }) {
  return (
    <Paper sx={{ p: 3, borderRadius: 2.5 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
        {subtitle}
      </Typography>
      {children}
    </Paper>
  );
}
